import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { 
  Plus, 
  CalendarDays,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Building2,
  Download,
  FileSpreadsheet,
  FileText,
  Info
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { useLeaves } from "../hooks";
import api from "../lib/api";

const leaveTypes = [
  { value: "annual", label: "Annual Leave" },
  { value: "sick", label: "Sick Leave" },
  { value: "personal", label: "Personal Leave" },
  { value: "maternity", label: "Maternity Leave" },
  { value: "paternity", label: "Paternity Leave" },
  { value: "unpaid", label: "Unpaid Leave" },
];

const Leaves = () => {
  const { currentBusiness, user } = useAuth();
  
  // Use custom hook for data fetching
  const { 
    leaves, 
    employees, 
    loading, 
    stats,
    createLeave, 
    approveLeave, 
    rejectLeave, 
    exportLeaves,
    getEmployeeName 
  } = useLeaves(currentBusiness?.id);

  // Local UI state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  
  // Export state
  const [exportStartDate, setExportStartDate] = useState(null);
  const [exportEndDate, setExportEndDate] = useState(null);
  const [exportEmployee, setExportEmployee] = useState("all");
  const [exportStatus, setExportStatus] = useState("all");
  const [exportFormat, setExportFormat] = useState("excel");
  const [exporting, setExporting] = useState(false);
  
  const [formData, setFormData] = useState({
    employee_id: "",
    leave_type: "",
    reason: ""
  });

  // Leave balance state
  const [employeeBalances, setEmployeeBalances] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Fetch leave balance when employee is selected
  const fetchEmployeeBalance = useCallback(async (employeeId) => {
    if (!employeeId) {
      setEmployeeBalances([]);
      return;
    }
    setBalanceLoading(true);
    try {
      const res = await api.get(`/leave-balance/${employeeId}`);
      setEmployeeBalances(res.data.balances || []);
    } catch (error) {
      // Error handled silently
      setEmployeeBalances([]);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  // Fetch balance when employee changes
  useEffect(() => {
    if (formData.employee_id && dialogOpen) {
      fetchEmployeeBalance(formData.employee_id);
    }
  }, [formData.employee_id, dialogOpen, fetchEmployeeBalance]);

  // Get balance for selected leave type
  const selectedBalance = employeeBalances.find(b => b.leave_type === formData.leave_type);
  
  // Calculate days requested
  const daysRequested = formData.leave_type ? Math.max(1, differenceInDays(endDate, startDate) + 1) : 0;
  
  // Check if insufficient balance
  const insufficientBalance = selectedBalance && selectedBalance.available < daysRequested && formData.leave_type !== "unpaid";

  // Helper function to get approve button text based on status
  const getApproveButtonText = (status) => {
    const statusLabels = {
      pending_hr: "HR Approve",
      rejected_by_dept: "HR Override",
      pending_dept_head: "Dept Approve",
    };
    return statusLabels[status] || "Approve";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createLeave({
        ...formData,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd")
      });
      toast.success("Leave request submitted");
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to submit leave request");
    }
  };

  const handleApprove = async (id) => {
    try {
      const res = await approveLeave(id);
      toast.success(res.message || "Leave approved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve leave");
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await rejectLeave(id);
      toast.success(res.message || "Leave rejected");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject leave");
    }
  };

  const handleExport = async () => {
    if (!exportStartDate || !exportEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    
    setExporting(true);
    try {
      const blob = await exportLeaves(exportStartDate, exportEndDate, exportFormat, exportEmployee, exportStatus);
      const downloadUrl = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const startStr = format(exportStartDate, "yyyy-MM-dd");
      const endStr = format(exportEndDate, "yyyy-MM-dd");
      const empName = exportEmployee !== "all" ? employees.find(e => e.id === exportEmployee) : null;
      const empPart = empName ? `_${empName.first_name}_${empName.last_name}` : "";
      const ext = exportFormat === "excel" ? "xlsx" : exportFormat;
      link.setAttribute('download', `leaves${empPart}_${startStr}_to_${endStr}.${ext}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export completed");
      setExportDialogOpen(false);
    } catch (error) {
      toast.error("Failed to export leaves");
    } finally {
      setExporting(false);
    }
  };

  const resetForm = () => {
    setFormData({ employee_id: "", leave_type: "", reason: "" });
    setStartDate(new Date());
    setEndDate(new Date());
    setEmployeeBalances([]);
  };

  const getLeaveTypeLabel = (value) => {
    return leaveTypes.find(t => t.value === value)?.label || value;
  };

  const canManageLeaves = ["super_admin", "business_admin", "hr_manager"].includes(user?.role);

  const getStatusBadge = (leave) => {
    const map = {
      pending_dept_head: { label: "Pending Dept Approval", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertCircle },
      pending_hr: { label: "Pending HR Approval", cls: "bg-blue-50 text-blue-700 border-blue-200", icon: Clock },
      pending: { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertCircle },
      rejected_by_dept: { label: "Dept Rejected (HR Can Override)", cls: "bg-orange-50 text-orange-700 border-orange-200", icon: XCircle },
      approved: { label: "Approved", cls: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle },
      rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
    };
    const s = map[leave.status] || map.pending;
    const Icon = s.icon;
    return (
      <div className="space-y-1">
        <Badge variant="outline" className={s.cls}>
          <Icon className="h-3 w-3 mr-1" />{s.label}
        </Badge>
        {leave.dept_head_name && leave.status !== "pending_dept_head" && (
          <p className="text-[10px] text-slate-400">Dept: {leave.dept_head_name}</p>
        )}
        {leave.hr_name && leave.status === "approved" && (
          <p className="text-[10px] text-slate-400">HR: {leave.hr_name}</p>
        )}
        {leave.hr_override && (
          <p className="text-[10px] text-violet-500 font-medium">HR Override</p>
        )}
        {leave.rejected_by_name && leave.status === "rejected" && (
          <p className="text-[10px] text-red-400">By: {leave.rejected_by_name}</p>
        )}
      </div>
    );
  };

  const isPending = (leave) => ["pending_dept_head", "pending_hr", "pending", "rejected_by_dept"].includes(leave.status);

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business to manage leaves</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leaves-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Leave Management</h1>
          <p className="text-slate-500">Track and manage leave requests</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {/* Export Dialog */}
          <Dialog open={exportDialogOpen} onOpenChange={(open) => { 
            setExportDialogOpen(open); 
            if (!open) {
              setExportStartDate(null);
              setExportEndDate(null);
              setExportEmployee("all");
              setExportStatus("all");
              setExportFormat("excel");
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="export-leaves-btn">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Download className="h-5 w-5 text-primary" />
                  Export Leave Report
                </DialogTitle>
                <DialogDescription>
                  Generate leave report in your preferred format
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Format Selection */}
                <div className="space-y-2">
                  <Label>Export Format</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={exportFormat === "excel" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setExportFormat("excel")}
                      className={exportFormat === "excel" ? "bg-green-600 hover:bg-green-700" : ""}
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-1" />
                      Excel
                    </Button>
                    <Button
                      variant={exportFormat === "csv" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setExportFormat("csv")}
                      className={exportFormat === "csv" ? "bg-blue-600 hover:bg-blue-700" : ""}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                    <Button
                      variant={exportFormat === "pdf" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setExportFormat("pdf")}
                      className={exportFormat === "pdf" ? "bg-red-600 hover:bg-red-700" : ""}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                </div>

                {/* Employee Selection */}
                <div className="space-y-2">
                  <Label>Employee</Label>
                  <Select value={exportEmployee} onValueChange={setExportEmployee}>
                    <SelectTrigger className="input-field">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Employees</SelectItem>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Selection */}
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={exportStatus} onValueChange={setExportStatus}>
                    <SelectTrigger className="input-field">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending (All)</SelectItem>
                      <SelectItem value="pending_dept_head">Pending Dept Approval</SelectItem>
                      <SelectItem value="pending_hr">Pending HR Approval</SelectItem>
                      <SelectItem value="rejected_by_dept">Dept Rejected (Awaiting HR)</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Date Range Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={exportStartDate ? format(exportStartDate, "yyyy-MM-dd") : ""}
                      onChange={(e) => setExportStartDate(e.target.value ? new Date(e.target.value) : null)}
                      className="input-field"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={exportEndDate ? format(exportEndDate, "yyyy-MM-dd") : ""}
                      onChange={(e) => setExportEndDate(e.target.value ? new Date(e.target.value) : null)}
                      className="input-field"
                    />
                  </div>
                </div>

                {/* Quick Date Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                    const today = new Date();
                    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                    setExportStartDate(firstDay);
                    setExportEndDate(today);
                  }}>This Month</Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                    const today = new Date();
                    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                    setExportStartDate(lastMonth);
                    setExportEndDate(lastDayLastMonth);
                  }}>Last Month</Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                    const today = new Date();
                    const firstDay = new Date(today.getFullYear(), 0, 1);
                    setExportStartDate(firstDay);
                    setExportEndDate(today);
                  }}>This Year</Button>
                </div>

                {/* Export Button */}
                <Button
                  className="w-full btn-primary"
                  onClick={handleExport}
                  disabled={!exportStartDate || !exportEndDate || exporting}
                >
                  {exporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Download {exportFormat.toUpperCase()} Report
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="request-leave-btn">
                <Plus className="h-4 w-4 mr-2" />
                Request Leave
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Request Leave</DialogTitle>
              <DialogDescription>Submit a new leave request</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Employee</Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                >
                  <SelectTrigger className="input-field" data-testid="leave-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Leave Type</Label>
                <Select
                  value={formData.leave_type}
                  onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
                >
                  <SelectTrigger className="input-field" data-testid="leave-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {employeeBalances.length > 0 ? (
                      employeeBalances.map((b) => (
                        <SelectItem key={b.leave_type} value={b.leave_type}>
                          <div className="flex items-center justify-between w-full gap-2">
                            <span>{b.name}</span>
                            <Badge variant="outline" className={b.available > 0 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}>
                              {b.available} days
                            </Badge>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      leaveTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {balanceLoading && (
                  <p className="text-xs text-slate-400">Loading balance...</p>
                )}
              </div>

              {/* Balance Info Card */}
              {selectedBalance && (
                <div className={`p-3 rounded-lg border ${insufficientBalance ? "bg-red-50 border-red-200" : "bg-violet-50 border-violet-200"}`}>
                  <div className="flex items-start gap-2">
                    <Info className={`h-4 w-4 mt-0.5 ${insufficientBalance ? "text-red-600" : "text-violet-600"}`} />
                    <div className="text-sm">
                      <p className={`font-medium ${insufficientBalance ? "text-red-700" : "text-violet-700"}`}>
                        {selectedBalance.name} Balance
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1 text-xs">
                        <p><span className="text-slate-500">Available:</span> <span className="font-medium">{selectedBalance.available} days</span></p>
                        <p><span className="text-slate-500">Requesting:</span> <span className="font-medium">{daysRequested} day{daysRequested !== 1 ? "s" : ""}</span></p>
                        <p><span className="text-slate-500">Accrual:</span> {selectedBalance.fixed_entitlement ? "Fixed" : `${selectedBalance.accrual_rate} day/month`}</p>
                        <p><span className="text-slate-500">Carry Forward:</span> {selectedBalance.carry_forward_allowed ? `Yes (max ${selectedBalance.max_carry_forward})` : "No"}</p>
                      </div>
                      {insufficientBalance && (
                        <p className="text-red-600 font-medium mt-2">
                          Insufficient balance! Employee needs {daysRequested - selectedBalance.available} more day(s).
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={format(startDate, "yyyy-MM-dd")}
                    onChange={(e) => e.target.value && setStartDate(new Date(e.target.value))}
                    className="input-field"
                    data-testid="leave-start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={format(endDate, "yyyy-MM-dd")}
                    onChange={(e) => e.target.value && setEndDate(new Date(e.target.value))}
                    className="input-field"
                    data-testid="leave-end-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Reason</Label>
                <Textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Enter reason for leave..."
                  className="input-field min-h-[80px]"
                  data-testid="leave-reason-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full btn-primary" 
                disabled={insufficientBalance}
                data-testid="submit-leave-btn"
              >
                {insufficientBalance ? "Insufficient Balance" : "Submit Request"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats - using hook's stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <CalendarDays className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                <p className="text-sm text-slate-500">Total Requests</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100">
                <Clock className="h-6 w-6 text-amber-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.pending}</p>
                <p className="text-sm text-slate-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.approved}</p>
                <p className="text-sm text-slate-500">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-red-100">
                <XCircle className="h-6 w-6 text-red-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.rejected}</p>
                <p className="text-sm text-slate-500">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leave Requests Table */}
      <Card className="card-soft" data-testid="leaves-table">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Leave Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : leaves.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No leave requests yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wide text-slate-400">Employee</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-400">Type</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-400">Duration</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-400">Reason</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-400">Status</TableHead>
                    {canManageLeaves && (
                      <TableHead className="text-xs uppercase tracking-wide text-slate-400 text-right">Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaves.map((leave) => (
                    <TableRow key={leave.id} className="hover:bg-slate-50/50" data-testid={`leave-row-${leave.id}`}>
                      <TableCell className="font-medium text-slate-800">
                        {getEmployeeName(leave.employee_id)}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {getLeaveTypeLabel(leave.leave_type)}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        <div className="flex items-center gap-1">
                          <CalendarDays className="h-4 w-4 text-slate-400" />
                          {leave.start_date} to {leave.end_date}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 max-w-[200px] truncate">
                        {leave.reason || "-"}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(leave)}
                      </TableCell>
                      {canManageLeaves && (
                        <TableCell className="text-right">
                          {isPending(leave) && (
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleApprove(leave.id)}
                                className="rounded-full text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`approve-leave-${leave.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                {getApproveButtonText(leave.status)}
                              </Button>
                              {leave.status !== "rejected_by_dept" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleReject(leave.id)}
                                  className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`reject-leave-${leave.id}`}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Reject
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Leaves;
